<template>

<div class="main list-view no-sub-header">

	<div class="filters">
		<div class="label-group">
			<label>Start</label>
			<input type="date" v-model="filters.start_date" :max="$today().clone().format('YYYY-MM-DD')">
		</div>
		<div class="label-group">
			<label>End</label>
			<input type="date" v-model="filters.end_date" :max="$today().clone().format('YYYY-MM-DD')">
		</div>
		<div class="label-group">
			<label></label>
			<div class="button small neutral" @click="getDepartmentTotals()">Filter Dates</div>
		</div>

		<div class="divider"></div>
		<div v-if="filters.department_id && Object.keys(categories).length > 0">
			<label>Category</label>
			<select class="medium filter" v-model="filters.category_id" @change="getDepartmentItems()">
				<option value="">Select...</option>
				<option v-for="(description, categoryId) in categories" :value="categoryId">{{ description }}</option>
			</select>
		</div>

		<label>Order Method</label>
		<select class="medium filter" v-model="filters.order_method_code" @change="getDepartmentTotals()">
			<option value="">Select...</option>
			<option value="web">Online</option>
			<option value="store">In Store</option>
			<option value="layaway">Layaway</option>
		</select>

		<div v-if="manufacturers.length > 0">
			<label>Manufacturer</label>
			<select class="medium filter" v-model="filters.manufacturer_id" @change="getDepartmentItems()">
				<option value="">Select...</option>
				<option v-for="(manufacturer, index) in manufacturers" :value="manufacturer.manufacturer_id">{{ manufacturer.description }}</option>
			</select>
		</div>

		<div class="divider"></div>
		<ul class="checkbox-group">
			<li v-if="!filters.department_id">
				<span :class="['pointer', 'far', filters.hide_empty ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('hide_empty')"></span>
				<label>Hide empty departments</label>
			</li>
			<li v-if="filters.department_id">
				<span :class="['pointer', 'far', filters.group_by_item ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('group_by_item')"></span>
				<label>Group by item</label>
			</li>
		</ul>

		<div class="divider"></div>
        <div class="button neutral" @click="clearFilters()">Clear Filters</div>
		<div v-if="filters.payment_method_id">
			<div v-if="orderDetail.order_id" class="button neutral" @click="orderDetail = {}">Back to List</div>
			<div v-else class="button neutral" @click="filters.payment_method_id = ''">Back to Report</div>
		</div>
		<div v-else>
			<div v-if="orderDetail.order_id && !extraDepartment" class="button neutral" @click="getDepartmentItems()">Back to List</div>
			<div v-else-if="orderDetail.order_id && extraDepartment" class="button neutral" @click="getExraDetail()">Back to List</div>
			<div v-else-if="departmentId" class="button neutral" @click="getDepartmentTotals()">Back to Report</div>
		</div>
		<div v-if="!orderDetail.order_id">
			<div class="divider"></div>
			<div v-if="filters.department_id || filters.payment_method_id" :class="['extra-menu-item', gettingExport ? 'disabled' : '']" @click="doExport()">Export CSV</div>
			<div v-else :class="['extra-menu-item', gettingExport ? 'disabled' : '']" @click="showExportType = true">Export CSV</div>
			<div class="extra-menu-item" @click="printWindow()">Print Report</div>
			<div class="extra-menu-item" @click="showHideDepartments = true">Hide Departments</div>
			<div v-if="getDevEnvironment" class="extra-menu-item" @click="getDepartmentTotals()">Reload Report</div>
		</div>
		<div v-if="legacyLink">
			<div class="divider"></div>
			<router-link :to="legacyLink" class="extra-menu-item">Legacy Version</router-link>
		</div>
		<div class="message">{{ exportMessage }}</div>

	</div>

	<div v-if="orderDetail.order_id" class="detail">
		<div v-if="loading" class="loading empty-list"><i class="fa fa-spinner fa-spin" /></div>
		<order-detail v-else :orderDetail="orderDetail" />
	</div>

	<div v-else-if="departmentId || filters.manufacturer_id" class="list report-list no-sub-header">
		<div class="printable-heading">Daily Cash Report: {{ filters.start_date }} through {{ filters.end_date }}</div>
		<div style="margin: 0 1em;">
			<div class="meta right">
				{{ filters.category_id ? 'Category' : 'Department' }} Total: <b>{{ getListTotal(itemList, 'total') }}</b>
			</div>
			<h3>Sales In {{ departmentName }} {{ categoryName }}</h3>
			<table class="data-list" cellpadding="0" cellspacing="0">
				<thead>
					<tr>
						<th v-if="!filters.group_by_item" class="list">Date</th>
						<th v-if="!filters.group_by_item" class="list">Order #</th>
						<th class="list">UPC</th>
						<th class="list">Description</th>
						<th class="list right">Units</th>
						<th class="list right">Cost</th>
						<th class="list right">Price</th>
						<th class="list right">Profit</th>
						<th class="list right">Margin</th>
						<th class="list right">Total</th>
					</tr>
				</thead>
				<tr v-for="item in itemList" class="clickable-list-item" @click="getOrder(item.order_id)">
					<td v-if="!filters.group_by_item" class="list">{{ $moment(item.order_date).format("MM/DD/YY") }}</td>
					<td v-if="!filters.group_by_item" class="list">{{ item.order_number }}</td>
					<td class="list">{{ item.upc }}</td>
					<td class="list">{{ item.description }}</td>
					<td :class="['list', 'right', item.units != 0 ? '' : 'empty-value']">{{ item.units }}</td>
					<td :class="['list', 'right', item.order_cost != 0 ? '' : 'empty-value']">{{ $formatCurrency(item.order_cost) }}</td>
					<td :class="['list', 'right', item.order_price != 0 ? '' : 'empty-value']">{{ $formatCurrency(item.order_price) }}</td>
					<td :class="['list', 'right', item.gross_profit != 0 ? '' : 'empty-value']">{{ $formatCurrency(item.gross_profit) }}</td>
					<td :class="['list', 'right', item.profit_percent != 0 ? '' : 'empty-value']">{{ $formatPercent(item.profit_percent) }}</td>
					<td :class="['list', 'right', item.total != 0 ? '' : 'empty-value']">{{ $formatCurrency(item.total) }}</td>
				</tr>

				<tr class="list_row_totals">
					<td class="list" :colspan="filters.group_by_item ? 2 : 4">{{ filters.category_id ? 'Category' : 'Department' }} Totals</td>
					<td class="list right">{{ getListTotal(itemList, 'units', false) }}</td>
					<td class="list right">{{ getListTotal(itemList, 'order_cost') }}</td>
					<td class="list right">{{ getListTotal(itemList, 'order_price') }}</td>
					<td class="list right">{{ getListTotal(itemList, 'gross_profit') }}</td>
					<td class="list right">{{ getListTotalProfitPercent('item_list') }}</td>
					<td class="list right">{{ getListTotal(itemList, 'total') }}</td>
				</tr>

			</table>
		</div>
	</div>

	<div v-else-if="filters.payment_method_id" class="list report-list no-sub-header">
		<div class="printable-heading">Daily Cash Report: {{ filters.start_date }} through {{ filters.end_date }}</div>
		<div style="margin: 0 1em;">
			<div class="meta right">
				Total: <b>{{ getListTotal(paymentTypeList, 'amount') }}</b>
			</div>
			<h3>Sales by {{ paymentTypeName }}</h3>
			<table class="data-list" cellpadding="0" cellspacing="0">
				<tr>
					<th class='list left'>Order Number</th>
					<th class='list left'>Customer</th>
					<th class='list left'>Date</th>
					<th class='list left'>Card Type</th>
					<th class='list left'>Auth Code</th>
					<th class='list right'>Amount</th>
				</tr>
				<tr v-for="item in paymentTypeList" class="clickable-list-item" @click="getOrder(item.order_id)">
					<td class="list left">{{ item.order_number }}</td>
					<td class="list left">{{ item.customer }}</td>
					<td class="list">{{ $moment(item.payment_date).format("MM/DD/YY") }}</td>
					<td class="list">{{ item.card_type }}</td>
					<td class="list">{{ item.authorization_code }}</td>
					<td :class="['list', 'right', item.total != 0 ? '' : 'empty-value']">{{ $formatCurrency(item.amount) }}</td>
				</tr>
			</table>
		</div>
	</div>

	<div v-else class="list report-list no-sub-header">

		<div class="printable-heading">{{ getClient.company_name }} Daily Cash Report {{ $moment(filters.start_date).format('M/D/YY') }}<span v-if="filters.end_date != filters.start_date"> through {{ $moment(filters.end_date).format('M/D/YY') }}</span></div>

		<div v-if="departmentList.length == 0" class="empty-list">This report is empty</div>
		<div v-else>
			<div style="margin: 0 1em;">

				<div class="meta right">
					Report Total: <b>{{ getGrandTotal('total') }}</b>
				</div>

				<h3>Sales By Department</h3>
				<table class="data-list" cellpadding="0" cellspacing="0" style="page-break-after: always;">
					<thead>
						<tr>
							<th class="list">Department</th>
							<th class="list right">Units</th>
							<th class="list right">Cost</th>
							<th class="list right">Price</th>
							<th class="list right">Profit</th>
							<th class="list right">Margin</th>
							<th class="list right">Total</th>
						</tr>
					</thead>
					<tr v-for="item in departmentList" :class="{'clickable-list-item': item.transactions != 0}" @click="getItem(item)">
						<td class="list">{{ item.description }}</td>
						<td :class="['list', 'right', item.transactions != 0 ? '' : 'empty-value']">{{ item.units || '0' }}</td>
						<td :class="['list', 'right', item.transactions != 0 ? '' : 'empty-value']">{{ $formatCurrency(item.order_cost) }}</td>
						<td :class="['list', 'right', item.transactions != 0 ? '' : 'empty-value']">{{ $formatCurrency(item.order_price) }}</td>
						<td :class="['list', 'right', item.transactions != 0 ? '' : 'empty-value']">{{ $formatCurrency(item.gross_profit) }}</td>
						<td :class="['list', 'right', item.transactions != 0 ? '' : 'empty-value']">{{ $formatPercent(item.profit_percent) }}</td>
						<td :class="['list', 'right', item.transactions != 0 ? '' : 'empty-value']">{{ $formatCurrency(item.total) }}</td>
					</tr>

					<tr class="list_row_totals">
						<td class="list">Department Totals</td>
						<td class="list right">{{ getListTotal(departmentList, 'units', false) }}</td>
						<td class="list right">{{ getListTotal(departmentList, 'order_cost') }}</td>
						<td class="list right">{{ getListTotal(departmentList, 'order_price') }}</td>
						<td class="list right">{{ getListTotal(departmentList, 'gross_profit') }}</td>
						<td class="list right">{{ getListTotalProfitPercent('department_list') }}</td>
						<td class="list right">{{ getListTotal(departmentList, 'total') }}</td>
					</tr>

					<tr v-for="item in extrasList" :class="{ 'clickable-list-item': item.order_price != 0 }" @click="goLink(item)">
						<td class="list">{{ item.description }}</td>
						<td class="list right"></td>
						<td class="list right"></td>
						<td :class="['list', 'right', item.order_price != 0 ? '' : 'empty-value']">{{ $formatCurrency(item.order_price) }}</td>
						<td class="list right"></td>
						<td class="list right"></td>
						<td :class="['list', 'right', item.total != 0 ? '' : 'empty-value']">{{ $formatCurrency(item.total) }}</td>
					</tr>

					<tr class="list_row_totals">
						<td class="list">Report Totals</td>
						<td class="list right"></td>
						<td class="list right">{{ getGrandTotal('order_cost') }}</td>
						<td class="list right">{{ getGrandTotal('order_price') }}</td>
						<td class="list right"></td>
						<td class="list right"></td>
						<td class="list right">{{ getGrandTotal('total') }}</td>
					</tr>

				</table>
			</div>

			<div v-if="!filters.order_method_code" class="two-columns" style="margin: 2em 1em;">
				<div style="border: 1px solid #ccc; padding: 0.5em 1em;">
					<h3>Payments Received By Type</h3>
					<table class="data-list">
						<thead>
							<tr>
								<th class="list">Type</th>
								<th class="list right">In Store</th>
								<th class="list right">Online</th>
								<th class="list right">Total</th>
							</tr>
						</thead>
						<tr v-for="item in salesByPaymentType" :class="{'clickable-list-item': item.total}" @click="getListByPaymentType(item)">
							<td class="list">{{ item.description }}</td>
							<td :class="['list', 'right', item.store_total && item.store_total != 0 ? '' : 'empty-value']">{{ $formatCurrency(item.store_total) }}</td>
							<td :class="['list', 'right', item.online_total && item.online_total != 0 ? '' : 'empty-value']">{{ $formatCurrency(item.online_total) }}</td>
							<td :class="['list', 'right', item.total && item.total != 0 ? '' : 'empty-value']">{{ $formatCurrency(item.total) }}</td>
						</tr>
						<tr class="list_row_totals">
							<td class="list">Totals</td>
							<td class="list right">{{ getListTotal(salesByPaymentType, 'store_total') }}</td>
							<td class="list right">{{ getListTotal(salesByPaymentType, 'online_total') }}</td>
							<td class="list right">{{ getListTotal(salesByPaymentType, 'total') }}</td>
						</tr>
					</table>
					<div v-if="layawayPayments" class="no-print" style="margin: 2.5em 0 0 0; padding: 0.5em; background-color: #e4e4e4; text-align: center;">
						<p><b>NOTE:</b> ${{ layawayPayments }} of these payments were applied to layaways.</p>
						<router-link to="/orders-sales/layaway-payments"><b>Layaway Payments detail...</b></router-link>
					</div>
				</div>
				<div style="border: 1px solid #ccc; padding: 0.5em 1em;">
					<div v-if="filters.start_date != filters.end_date" class="note" style="float: right; margin: 0.75em 0 0 0;">Multi-day report excludes Open / Close amounts</div>
					<h3>Cash By Register</h3>
					<table class="data-list">
						<thead>
							<tr>
								<th class="list">Register</th>
								<th class="list right">Open</th>
								<th class="list right">Sales</th>
								<th class="list right">Close</th>
								<th class="list right">+ / -</th>
							</tr>
						</thead>
						<tr v-for="item in cashByRegister">
							<td class="list">{{ item.device_nickname }}</td>
							<td :class="['list', 'right', item.opening_amount && item.opening_amount != 0 ? '' : 'empty-value']">{{ $formatCurrency(item.opening_amount) }}</td>
							<td :class="['list', 'right', item.total != 0 ? '' : 'empty-value']">{{ $formatCurrency(item.total) }}</td>
							<td :class="['list', 'right', item.closing_amount && item.closing_amount != 0 ? '' : 'empty-value']">{{ $formatCurrency(item.closing_amount) }}</td>
							<td :class="['list', 'right', item.over_under && item.over_under != 0 ? '' : 'empty-value']">{{ $formatCurrency(item.over_under) }}</td>
						</tr>
						<tr class="list_row_totals">
							<td class="list">Totals</td>
							<td class="list right">{{ getListTotal(cashByRegister, 'opening_amount') }}</td>
							<td class="list right">{{ getListTotal(cashByRegister, 'total') }}</td>
							<td class="list right">{{ getListTotal(cashByRegister, 'closing_amount') }}</td>
							<td class="list right">{{ getListTotal(cashByRegister, 'over_under') }}</td>
						</tr>
					</table>
				</div>
			</div>

			<div v-if="!filters.order_method_code" class="two-columns" style="margin: 0 1em;">
				<div style="border: 1px solid #ccc; padding: 0.5em 1em;">
					<h3>Tax Summary</h3>
						<table class="data-list">
							<tr><td><i class="fa fa-info-circle pointer" @click="showTaxableInfo = 'tax_rate'"></i> Your Tax Rate</td><td class="right">{{ multipleTaxRates ? 'Multiple Rates' : taxRate }}</td></tr>
							<tr><td><i class="fa fa-info-circle pointer" @click="showTaxableInfo = 'tax_charged'"></i> Sales Tax Charged</td><td class="right">{{ $formatCurrency(taxCharged) }}</td></tr>
							<tr><td><i class="fa fa-info-circle pointer" @click="showTaxableInfo = 'estimated_taxed'"></i> Taxed Sales</td><td class="right"> <span v-if="loadingTaxSummary"><i class="fa fa-spinner fa-spin"></i></span> <span v-else>{{ taxableTotals.taxable ? $formatCurrency(taxableTotals.taxable) : '$0.00' }}</span></td></tr>
							<tr v-if="transferProductsTotal > 0"><td><i class="fa fa-info-circle pointer" @click="showTaxableInfo = 'transfer_products'"></i> Transfer Products</td><td class="right">{{ $formatCurrency(transferProductsTotal) }}</td></tr>
							<tr v-if="transferProductsTotal > 0"><td><i class="fa fa-info-circle pointer" @click="showTaxableInfo = 'combined_taxed_sales'"></i> Combined Taxed Sales</td><td class="right">{{ $formatCurrency(taxableTotals.taxable + transferProductsTotal) }}</td></tr>
							<tr><td><i class="fa fa-info-circle pointer" @click="showTaxableInfo = 'estimated_non_taxed'"></i> Not Taxed Sales</td><td class="right"> <span v-if="loadingTaxSummary"><i class="fa fa-spinner fa-spin"></i></span> <span v-else>{{ taxableTotals.not_taxable ? $formatCurrency(taxableTotals.not_taxable) : '$0.00' }}</span></td></tr>
						</table>
						<div v-if="showDiscrepancy()" class="no-print center" style="font-size: 0.8em;">
							<i class="fas fa-exclamation-triangle"></i> Calculated Taxed Sales: {{ taxCharged }} / {{ multipleTaxRates ? 'multiple rates' : taxRate }} = {{ $formatCurrency(taxedSales) }}
						</div>
						<div class="no-print" style="margin: 1.5em 0 0 0; padding: 0.5em; background-color: #e4e4e4; text-align: center;">
							<p>Use the Taxed Item Detail Report for a complete list of items sold and the amount that was taxed during this period.</p>
							<div class="clickable" @click="goTaxedItemDetailReport()"><b>Taxed Item Detail Report...</b></div>
						</div>
				</div>

				<div style="border: 1px solid #ccc; padding: 0.5em 1em;">
					<h3>Income Summary</h3>
					<table class="data-list">
						<tr><td>Credit Card Payments</td><td class="right">{{ $formatCurrency(totalPaymentsCreditCard) }}</td></tr>
						<tr><td>Cash Payments</td><td class="right">{{ $formatCurrency(totalPaymentsCash) }}</td></tr>
						<tr><td>Other Payments</td><td class="right">{{ $formatCurrency(totalPaymentsOther) }}</td></tr>
						<tr><td><b>Total Payments</b></td><td class="right"><b>{{ getListTotal(salesByPaymentType, 'total') }}</b></td></tr>
					</table>
					<div style="height: 1em;"></div>
					<table class="data-list">
						<tr><td>Department Sales</td><td class="right">{{ getListTotal(departmentList, 'total') }}</td></tr>
						<tr><td>Other Charges / Discounts</td><td class="right">{{ $formatCurrency(totalOtherCharges) }}</td></tr>
						<tr><td><b>Total Sales</b></td><td class="right"><b>{{ getGrandTotal('total') }}</b></td></tr>
					</table>
				</div>
			</div>
		</div>
	</div>

	<message-modal v-if="message" :message="message" :loading="loading" :errorStatus="messageStatus" :timeout="messageTimeout" @dismiss="message = ''" />

	<div v-if="showExportType" class="modal">
		<div class="inner">
			<h2>Export CSV</h2>
			<div class="content">
				<p>What do you want to export?</p>
				<select v-model="exportType">
					<option value="">Select...</option>
					<option value="department">Sales by Department</option>
					<option value="items">All Sale Items</option>
				</select>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="showExportType = false">Cancel</div>
				<div :class="['button', 'spaced', saving || !exportType ? 'disabled' : 'action']" @click="doExport()">Do Export</div>
			</div>
		</div>
	</div>

	<div v-if="showHideDepartments" class="modal">
		<div class="inner">
			<h2>Hide Departments</h2>
			<div class="content">
				<p>Select one or more departments to hide from this report.</p>
				<select v-model="hideDepartmentId" @change="setHiddenDepartment()">
					<option value="">Select...</option>
					<option v-for="(department, index) in departments" :value="department.department_id">{{ department.description }}</option>
				</select>
				<p>Hide these departments...</p>
				<div class="two-columns">
					<div v-for="(hidden, index) in hiddenDepartments"><span style="cursor: pointer; margin: 0 0.5em 0 0; color: #666;" @click="unsetHiddenDepartment(hidden.department_id)"><i class="fa fa-trash" /></span>{{ hidden.description }}</div>
				</div>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="showHideDepartments = false">Cancel</div>
				<div :class="['button', 'spaced', saving ? 'disabled' : 'action']" @click="saveHideDepartments()">Save</div>
			</div>
		</div>
	</div>

	<div v-if="showTaxableInfo" class="modal">
		<div class="inner">
			<h2>Taxed Sales</h2>
			<div class="content">
				<div v-if="showTaxableInfo == 'tax_rate'">
					<h3>Your Tax Rate</h3>
					This is the tax rate currently being charged on your store's taxable items and transactions. <router-link to="/website/checkout"><b>Click here</b></router-link> to change your tax rate.
				</div>
				<div v-if="showTaxableInfo == 'tax_charged'">
					<h3>Sales Tax Charged</h3>
					This is the sum of sales tax charges from transactions in the selected date range for this report.
				</div>
				<div v-if="showTaxableInfo == 'estimated_taxed'">
					<h3>Taxed Sales</h3>
					This the total of taxed sales for the selected date range.
				</div>
				<div v-if="showTaxableInfo == 'transfer_products'">
					<h3>Transfer Products</h3>
					This is the total value of all Transfer Products recorded in POS during the selected time period. Transfer Products are used to charge sales tax on firearm transfers from out-of-state in states that require dealers to collect that tax.
				</div>
				<div v-if="showTaxableInfo == 'combined_taxed_sales'">
					<h3>Combined Taxed Sales</h3>
					This is the number that results from adding the totals for Taxed Sales and Transfer Products for the selected time period.
				</div>
				<div v-if="showTaxableInfo == 'estimated_non_taxed'">
					<h3>Not Taxed Sales</h3>
					This is the total of not taxed sales for the selected date range.
				</div>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="showTaxableInfo = false">Got It!</div>
			</div>
		</div>
	</div>

	<iframe id="download" src="" style="display: none;"></iframe>

	<help-popup :helpLink="{223:'Daily Cash Report', 225:'Totals Sections'}" :tours="false"></help-popup>

</div>

</template>

<script>
import OrderDetail from '@/components/OrderDetail.vue'
import HelpPopup from '@/components/HelpPopup.vue'

export default {
	name: 'daily-cash-report',
    components: {
        'order-detail': OrderDetail,
		'help-popup': HelpPopup
    },
	data() {
		return {
            loading: true,
			message: '',
			messageStatus: '',
			messageTimeout: 0,
			filters: {},
            default_start_date: '',
            default_end_date: '',
			departmentList: [],
			departments: [],
			hiddenDepartments: [],
			showHideDepartments: false,
			hideDepartmentId: '',
			saving: false,
			loadingExtras: false,
			extrasList: [],
			extraDepartment: '',
			salesByPaymentType: {},
			layawayPayments: 0,
			cashByRegister: {},
			taxRate: '',
			multipleTaxRates: false,
			taxableTotals: {},
			taxCharged: 0,
			taxedSales: 0,
			nonTaxedSales: 0,
			totalPaymentsCreditCard: 0,
			totalPaymentsCash: 0,
			totalPaymentsOther: 0,
			totalOtherCharges: 0,
			departmentId: '',
			departmentName: '',
			categoryName: '',
			itemList: [],
			categories: {},
			paymentTypeList: [],
			paymentTypeName: '',
			manufacturers: [],
			manufacturerName: '',
			orderDetail: {},
			exportMessage: '',
			gettingExport: false,
			legacyLink: '',
			showTaxableInfo: '',
			transferProductsTotal: 0,
			showExportType: false,
			exportType: '', 
			loadingTaxSummary: false
		}
	},
	created() {
		this.clearFilters()
	},
	beforeRouteLeave(to, from, next) {
		// store filters in session storage
		sessionStorage.setItem('daily_cash_report_filters', JSON.stringify(this.filters))
		next()
	},
	methods: {
		showDiscrepancy() {
			if (this.filters.start_date == this.filters.end_date) {
				if (Math.abs(this.taxableTotals.taxable - this.taxedSales) >= 1) {
					return true
				}
			}
			return false
		},
		clearFilters() {
			this.filters = {
				start_date: '',
				end_date: '',
				order_method_code: '',
				payment_method_id: '',
				department_id: '',
				category_id: '',
				manufacturer_id: '',
				group_by_item: false,
				hide_empty: false,
				do_export: false
			}
			this.categories = {}
			this.default_start_date = this.$today().clone().format('YYYY-MM-DD')
			this.default_end_date = this.$today().clone().format('YYYY-MM-DD')
			this.filters.start_date = this.$route.query.start || this.default_start_date
			this.filters.end_date = this.$route.query.end || this.default_end_date
			this.getDepartmentTotals()
		},
		toggleFilter(which) {
			this.filters[which] = !this.filters[which]
			if (this.filters.department_id) {
				this.getDepartmentItems()
			} else {
				this.getDepartmentTotals()
			}
		},
		getDepartmentTotals() {
			this.departmentId = ''
			this.filters.department_id = ''
			this.filters.category_id = ''
			this.filters.payment_method_id = ''
			this.orderDetail = {}
			this.loading = true
			// get number of days between start and end dates
			let start = new Date(this.filters.start_date)
			let end = new Date(this.filters.end_date)
			let days = Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1
			this.message = 'Generating report'
			if (days > 366) {
				this.message = 'Date range greater than one year not supported.'
				this.messageStatus = 'error'
				this.loading = false
				return
			}
			if (days > 1) {
				this.message += ' for ' + days + ' days'
			}
			if (days > 90) {
				this.message += '<br>(if this takes too long to load, try a smaller date range)'
			}
			this.message += '...'
			if (days > 20) {
				this.message += '<br>(this may take a few minutes)'
			}
			this.apiCall({ destination: 'get_report_daily_cash_departments', data: this.filters }).then(obj => {
				this.messageStatus = obj.status
				this.exportType = ''
				if (this.filters.do_export) {
					this.loading = false
					this.message = ''
					this.filters.do_export = 0
					// accommodate live server filecache latency
					setTimeout(() => {
						document.getElementById("download").src = obj.download_url
						this.gettingExport = false
						this.exportMessage = ''
					}, 500)
				} else {
					if (obj.hide_empty) {
						this.filters.hide_empty = true
					}
					this.departmentList = obj.department_list
					this.extrasList = obj.extras_list
					// does extrasList contain an element where link = 'TRANSFERS'?
					this.transferProductsTotal = 0
					this.extrasList.forEach((extra) => {
						if (extra.link == 'TRANSFERS') {
							this.transferProductsTotal = parseFloat(extra.order_price)
						}
					})
					// for hide departments dropdown
					this.departments = obj.departments
					this.hiddenDepartments = obj.hidden_departments
					// don't get extras if filtered on an order method
					if (this.filters.order_method_code) {
						this.message = ''
						this.loading = false
					} else {
						if (obj.legacy_link) {
							this.legacyLink = obj.legacy_link
						}
						this.getExtrasTotals()
						this.getTaxableTotals()
					}
				}
			}).catch(err => {
				//console.log(err)
				this.loading = false
				this.messageStatus = 'error'
				this.message = "There was an error loading this report."
			})
		},
		getListTotal(whichList, column, format = true) {
			let total = 0
			if (typeof whichList === 'object') {
				for (const [key, row] of Object.entries(whichList)) {
					if (row[column]) {
						total += parseFloat(row[column])
					}
				}
			} else {
				whichList.forEach((row) => {
					if (row[column]) {
						total += parseFloat(row[column])
					}
				})
			}
			if (format) {
				return this.$formatCurrency(total)
			} else {
				return Math.round(total * 100) / 100
			}
		},
		getListTotalProfitPercent(which) {
			//number_format(100 * ( 1 - ( $row['cost'] / $row['price'])), 1, '.', '')
			let totalCost = 0
			let totalPrice = 0
			let totalProfitPercent = 0
			if (which == 'department_list') {
				this.departmentList.forEach((department) => {
					if (department['order_cost']) {
						totalCost += parseFloat(department['order_cost'])
					}
					if (department['order_price']) {
						totalPrice += parseFloat(department['order_price'])
					}
				})
			} else {
				this.itemList.forEach((item) => {
					if (item['order_cost']) {
						totalCost += parseFloat(item['order_cost'])
					}
					if (item['order_price']) {
						totalPrice += parseFloat(item['order_price'])
					}
				})
			}
			if (totalPrice > 0) {
				totalProfitPercent = (100 * ( 1 - ( totalCost / totalPrice)))
			}
			return this.$formatPercent(totalProfitPercent)
		},
		getGrandTotal(which, format = true) {
			let total = 0
			this.departmentList.forEach((department) => {
				if (department[which]) {
					total += parseFloat(department[which])
				}
			})
			this.extrasList.forEach((extra) => {
				if (extra[which]) {
					total += parseFloat(extra[which])
				}
			})
			if (format) {
				return this.$formatCurrency(total)
			} else {
				return Math.round(total * 100) / 100
			}
		},
		getExtrasTotals() {
			if (this.loadingExtras) { return }
			this.loadingExtras = true
			this.apiCall({ destination: 'get_report_daily_cash_details', data: this.filters }).then(obj => {
				this.loadingExtras = false
				this.salesByPaymentType = obj.sales_by_payment_type
				this.cashByRegister = obj.cash_by_register
				this.taxRate = obj.tax_rate
				this.layawayPayments = obj.layaway_payments
				this.manufacturers = obj.manufacturers
				this.calculateTaxableSales()
				this.calculateIncomeSummary()
				this.loading = false
				this.message = ''
			}).catch(err => {
				this.loading = false
				this.messageStatus = 'error'
				this.message = "There was an error loading this report."
			})
		},
		getTaxableTotals() {
			this.loadingTaxSummary = true
			this.taxableTotals = {
				taxable: 0,
				not_taxable: 0
			}
			this.getTaxableTotalsChunk(this.filters.start_date, this.filters.end_date)
			return
		},
		getTaxableTotalsChunk(start, end) {
			let totalsFilters = JSON.parse(JSON.stringify(this.filters))
			totalsFilters.start_date = start
			totalsFilters.end_date = end
			totalsFilters.totals_only = true
			this.apiCall({ destination: 'get_report_order_items', data: totalsFilters }).then(obj => {
				this.taxableTotals.taxable += obj.totals.taxable
				this.taxableTotals.not_taxable += obj.totals.not_taxable
				if (obj.tax_rates && obj.tax_rates.length > 1) {
					this.multipleTaxRates = true
				}
			}).catch(err => {
				this.loading = false
				this.messageStatus = 'error'
				this.message = "There was an error loading this report."
			}).finally(() => {
				this.loadingTaxSummary = false
			})
		},
		calculateTaxableSales() {
			this.taxCharged = 0
			this.taxedSales = 0
			this.nonTaxedSales = 0
			this.extrasList.forEach((row) => {
				if (row['description'] == 'Sales Tax') {
					if (row['order_price'] > 0) {
						this.taxCharged = parseFloat(row['order_price'])
					}
				}
			})
			if (this.taxCharged > 0) {
				this.taxedSales = this.taxCharged / this.taxRate
				// this.nonTaxedSales = this.taxableTotals.not_taxable
			}
		},
		calculateIncomeSummary() {
			this.totalPaymentsCreditCard = 0
			this.totalPaymentsCash = 0
			this.totalPaymentsOther = 0
			for (const [key, row] of Object.entries(this.salesByPaymentType)) {
				if ((key.indexOf('CREDIT') > -1 || key.indexOf('DEBIT') > -1) && row.total) {
					this.totalPaymentsCreditCard += parseFloat(row.total)
				} else if (key.indexOf('CASH') > -1 && row.total) {
					this.totalPaymentsCash += parseFloat(row.total)
				} else if (row.total) {
					this.totalPaymentsOther += parseFloat(row.total)
				}
			}
			this.totalOtherCharges = 0
			this.extrasList.forEach((row) => {
				if (row.total) {
					// layaway and subscriptions are listed under payments!
					if (row['description'].indexOf('Layaway') > -1 || row['description'].indexOf('Subscription') > -1) {
						this.totalPaymentsOther += parseFloat(row.total)
					} else {
						this.totalOtherCharges += parseFloat(row.total)
					}
				}
			})
		},
		getItem(item) {
			if (item.transactions == 0) { return }
			if (item.link) {
				this.goLink(item)
				return
			}
			this.departmentId = item.department_id
			this.departmentName = item.description
			this.filters.department_id = item.department_id
			this.getDepartmentItems()
		},
		getDepartmentItems() {
			if (this.loading) { return }
			if (!this.filters.department_id && !this.filters.manufacturer_id && this.filters.export_type != 'items') { 
				this.getDepartmentTotals()
				return
			}
			this.loading = true
			if (this.exportType == 'items') {
				this.message = 'Loading all sale items...'
			} else {
				this.message = 'Loading items...'
			}
			this.orderDetail = {}
			this.extraDepartment = ''
			this.apiCall({ destination: 'get_report_daily_cash_department', data: this.filters }).then(obj => {
				this.loading = false
				this.message = ''
				this.exportType = ''
				if (this.filters.do_export) {
					this.filters.do_export = 0
					// accommodate live server filecache latency
					setTimeout(() => {
						document.getElementById("download").src = obj.download_url
						this.gettingExport = false
						this.exportMessage = ''
					}, 500)
				} else {
					if (obj.group_by_item) {
						this.filters.group_by_item = true
					}
					this.itemList = obj.list
					this.categoryName = ''
					// filter this.categories where key = category_id
					if (this.filters.category_id) {
						for (const [key, value] of Object.entries(this.categories)) {
							if (key == this.filters.category_id) {
								this.categoryName = value
							}
						}
					}
					if (obj.categories) {
						this.categories = obj.categories
					}
				}
			}).catch(err => {
				console.log(err)
			})
		},
		goLink(item) {
			if (item.order_price == 0) { return }
			// does it look like a router link?
			if (item.link.indexOf("/") > -1) {
				this.$router.push(item.link + '?start=' + this.filters.start_date + '&end=' + this.filters.end_date)
			} else {
				this.departmentId = item.link
				this.departmentName = item.description
				this.filters.department_id = item.link
				this.getExraDetail()
			}
		},
		goTaxedItemDetailReport() {
			// console.log(this.$route)
			// return
			let returnRoute = this.$route.path + '?start=' + this.filters.start_date + '&end=' + this.filters.end_date
			sessionStorage.setItem("returnRoute", returnRoute);
			this.$router.push('/reports/order-items?start=' + this.filters.start_date + '&end=' + this.filters.end_date)
		},
		getListByPaymentType(paymentType) {
			if (!paymentType.total) { return }
			if (this.loading) { return }
			this.loading = true
			this.paymentTypeName = paymentType.description
			this.message = 'Loading ' + this.paymentTypeName + ' items...'
			this.orderDetail = {}
			this.filters.payment_method_id = paymentType.payment_method_id
			this.filters.payment_method_code = paymentType.payment_method_code
			this.apiCall({ destination: 'get_report_daily_cash_payment_type', data: this.filters }).then(obj => {
				this.message = obj.message
				this.loading = false
				if (this.filters.do_export) {
					this.filters.do_export = 0
					// accommodate live server filecache latency
					setTimeout(() => {
						document.getElementById("download").src = obj.download_url
						this.gettingExport = false
						this.exportMessage = ''
					}, 500)
				} else {
					this.paymentTypeList = obj.list
				}
			}).catch(err => {
				console.log(err)
			})
		},
		getExraDetail() {
			if (this.loading) { return }
			this.loading = true
			this.message = 'Loading items...'
			this.orderDetail = {}
			this.apiCall({ destination: 'get_report_daily_cash_extras', data: this.filters }).then(obj => {
				this.message = ''
				this.loading = false

				if (this.filters.do_export) {
					this.filters.do_export = 0
					// accommodate live server filecache latency
					setTimeout(() => {
						document.getElementById("download").src = obj.download_url
						this.gettingExport = false
						this.exportMessage = ''
					}, 500)
				} else {
					this.itemList = obj.list
					this.extraDepartment = this.departmentId
				}
			}).catch(err => {
				console.log(err)
			})
		},
		getOrder(orderId) {
			if (this.loading || !orderId) { return }
			this.loading = true
			this.orderDetail = { order_id: orderId }
			this.apiCall({ destination: 'get_item_order', data: { order_id: orderId } }).then(obj => {
				this.orderDetail.order = obj.order_detail
				this.orderDetail.billing = obj.order_billing_address
				this.orderDetail.custom_fields = obj.order_billing_custom_fields
				this.orderDetail.shipping = obj.order_shipping_address
				this.orderDetail.items = obj.order_items
				this.orderDetail.discounts = obj.order_discounts
				this.orderDetail.fees = obj.order_fees
				this.orderDetail.payments = obj.order_payments
				this.orderDetail.totals = obj.order_totals
				this.loading = false
			})
		},
		printWindow() {
			window.print()
		},
		setHiddenDepartment() {
			// is this department already in the hide list?
			let alreadyHidden = false
			this.hiddenDepartments.forEach((row, index) => {
				if (row.department_id == this.hideDepartmentId) {
					alreadyHidden = true
				}
			})
			if (!alreadyHidden) {
				this.departments.forEach((row, index) => {
					if (row.department_id == this.hideDepartmentId) {
						this.hiddenDepartments.push(row)
					}
				})
			}
		},
		unsetHiddenDepartment(departmentId) {
			let unsetIndex = -1
			this.hiddenDepartments.forEach((row, index) => {
				if (row.department_id == departmentId) {
					unsetIndex = index
				}
			})
			if (unsetIndex > -1) {
				this.hiddenDepartments.splice(unsetIndex, 1)
			}
		},
		saveHideDepartments() {
			this.apiCall({ destination: 'save_item_preference', data: { preference_code: 'CASH_REPORT_HIDE_DEPARTMENTS', value: JSON.stringify(this.hiddenDepartments) } }).then(obj => {
				this.showHideDepartments = false
				this.hideDepartmentId = ''
				this.getDepartmentTotals()
			})
		},
		doExport() {
			if (this.gettingExport) { return false }
			this.showExportType = false
			this.gettingExport = true
			this.filters.do_export = true
			this.filters.export_type = this.exportType
			if (this.filters.payment_method_id) {
				this.exportMessage = 'Generating CSV for ' + this.paymentTypeList.length + ' items...'
				this.getListByPaymentType({'payment_method_id': this.filters.payment_method_id, 'description': this.paymentTypeName, 'total': 1})
			} else if (this.extraDepartment) {
				this.exportMessage = 'Generating CSV for ' + this.extrasList.length + ' items...'
				this.getExraDetail()
			} else if (this.departmentId) {
				this.exportMessage = 'Generating CSV for ' + this.itemList.length + ' items...'
				this.getDepartmentItems()
			} else if (this.exportType == 'items') {
				this.exportMessage = 'Generating CSV for all sale items...'
				this.getDepartmentItems()
			} else {
				this.exportMessage = 'Generating CSV for ' + this.departmentList.length + ' deptarmtent totals...'
				this.getDepartmentTotals()
			}
		}
	}
}
</script>


